import { mapGetters } from 'vuex';
import { Loading } from 'element-ui';
import { report } from '@/api/report';
import { queryTags, createRole, queryRoleInfo } from '@/api/role';
import Navigation from '@/components/Navigation/Navigation.vue';
import OpenApp from '@/components/OpenApp/OpenApp.vue';
import Button from '@/components/Button/Button.vue';
import Dialog from '@/components/Dialog/Dialog.vue';

function isEqualSize(file) {
	const reader = new FileReader();

	return new Promise((resolve, reject) => {
		reader.onload = (e) => {
			const img = new Image();

			img.onload = () => {
				const width = img.width;
	      const height = img.height;

	      resolve({
	      	equal: width === height,
	      	width,
	      	height
	      });
			}

			img.src = e.target.result;
		};

		reader.readAsDataURL(file);
	});
}

// 文件格式校验
const validateFileType = (rule, value, callback) => {
  const allowedTypes = ['jpg', 'png', 'jpeg'];
  const invalidFiles = value.filter(file => {
    const fileSuffix = file.name.split('.').pop().toLowerCase();

    return !allowedTypes.includes(fileSuffix);
  });

  if (invalidFiles.length > 0) {
    callback(new Error(`Invalid file format. Only ${allowedTypes.join('、')} formats are supported.`));
    return;
  }

  callback();
};

// 文件宽高校验
const validateFileSize = async (rule, value, callback) => {
	const file = value[0];
	const { equal, width, height } = await isEqualSize(file);

	if (!equal) {
		callback(new Error(`The image must have equal width and height. Current dimensions: ${width}x${height}`));
    return;
	}

  callback();
};

// 体积大小校验
const validateFileSpaceSize = async (rule, value, callback) => {
	const file = value[0];
	const isLt2M = file.size / 1024 / 1024 <= 2;

	if (!isLt2M) {
		callback(new Error(`The file size must not exceed 2MB.`));
    return;
	}

  callback();
};

const validateNameLength = (rule, value, callback) => {
	if (value.length > 32) {
		callback(new Error(`Character name can't be longer than 32 characters.`));
    return;
	}

	callback();
}

const validateDescriptionLength = (rule, value, callback) => {
	if (value.length > 1000) {
		callback(new Error(`Character description can't be longer than 1000 characters.`));
    return;
	}

	callback();
}

const validateBaseInformationLength = (rule, value, callback) => {
	if (value.length > 15000) {
		callback(new Error(`Character base infomation can't be longer than 15000 characters.`));
    return;
	}

	callback();
}

const validateFirstMessageLength = (rule, value, callback) => {
	if (value.length > 5000) {
		callback(new Error(`Character first message can't be longer than 5000 characters.`));
    return;
	}

	callback();
}

const validategalleriesLength = (rule, value, callback) => {
	if (value.length < 4) {
		callback(new Error(`Upload at least 4 pictures.`));
    return;
	}

	callback();
}

const validateTagLength = (rule, value, callback) => {
	if (value.length < 5) {
		callback(new Error(`At least 5 tags.`));
    return;
	}

	callback();
}

const validateAvatarRequire = (rule, value, callback) => {
	if (value === '') {
		callback(new Error(`The character avatar cannot be empty.`));
    return;
	}

	callback();
}

export default {
	name: 'CreateCharacter',

	data() {
		return {
			uploadLoading: null,
			submitLoading: null,
			initLoading: null,
			isEdit: false,
			tags: [],
			tagAll: [],
			formData: {
				avatar_url: '',
				avatar_width: 0,
				avatar_height: 0,
				avatar_size: 0,
				name: '',
				description: '',

				// 0-其他 1-男 2-女
				gender: 'Female',
				tags: [],
				baseInformation: '',
				firstMessage: '',
				galleries: [],
			},
			rules: {
				avatar_url: [
					{ validator: validateAvatarRequire, trigger: ['change', 'blur'] },
				],

				name: [
					{ required: true, message: 'The character name cannot be empty.', trigger: ['change', 'blur'] },
					{ validator: validateNameLength, trigger: ['change', 'blur'] },
				],

				description: [
					{ required: true, message: 'The character description cannot be empty.', trigger: ['change', 'blur'] },
					{ validator: validateDescriptionLength, trigger: ['change', 'blur'] },
				],

				tags: [
					{ required: true, message: 'The character tags cannot be empty.', trigger: ['change', 'blur'] },
					{ validator: validateTagLength, trigger: ['change', 'blur'] },
				],

				baseInformation: [
					{ required: true, message: 'The character base infomation cannot be empty.', trigger: ['change', 'blur'] },
					{ validator: validateBaseInformationLength, trigger: ['change', 'blur'] },
				],

				firstMessage: [
					{ required: true, message: 'The character first message cannot be empty.', trigger: ['change', 'blur'] },
					{ validator: validateFirstMessageLength, trigger: ['change', 'blur'] },
				],

				galleries: [
					{ required: true, message: 'The character galleries cannot be empty.', trigger: ['change', 'blur'] },
					{ validator: validategalleriesLength, trigger: ['change', 'blur'] },
				]
			}
		};
	},

	computed: {
    ...mapGetters(['inviteCode', 'userToken']),
  },

	methods: {
		async init() {
			this.initLoading = Loading.service({
		    fullscreen: true,
	      customClass: 'text-loading',
	      text: 'Loading...'
		  });

		  this.reset();

			await this.createTagList();

			const { edit, character_id } = this.$route.query;

			if (edit) {
				this.isEdit = true;

				try {
					const res = await queryRoleInfo({
						id: character_id,
						token: this.userToken
					});

					this.fillFormData(res.data);
				}
				catch(err) {}
			}

			this.initLoading.close();
		},

		reset() {
			this.tags = [];
			this.tagAll = [];
			this.formData = {
				avatar_url: '',
				avatar_width: 0,
				avatar_height: 0,
				avatar_size: 0,
				name: '',
				description: '',

				// 0-其他 1-男 2-女
				gender: 'Female',
				tags: [],
				baseInformation: '',
				firstMessage: '',
				galleries: [],
			};
		},

		fillFormData(srcData) {
			this.formData.avatar_url = srcData.avatar.url;
			this.formData.avatar_width = srcData.avatar.width;
			this.formData.avatar_height = srcData.avatar.height;
			this.formData.avatar_size = srcData.avatar.size;
			this.formData.name = srcData.name;
			this.formData.description = srcData.description;
			this.formData.gender = srcData.gender;
			this.formData.tags = srcData.tags.map((tag) => {
				return tag.name;
			});
			this.formData.baseInformation = srcData.base_information;
			this.formData.firstMessage = srcData.first_message;
			this.formData.galleries = srcData.galleries.map((item) => {
				return {
					...item
				}
			});
		},

		async createTagList() {
			try {
				const res = await queryTags({
					token: this.userToken
				});

				this.tagAll = res.data.list;
				this.renderPartOfTags(20);
			}
			catch(err) {}
		},

		renderPartOfTags(count) {
			for (let i = 0; i < this.tagAll.length; i++) {
				if (i < count) {
					this.tags.push(this.tagAll[i]);
					continue;
				}

				break;
			}
		},

		showMoreTags() {
			this.tags = [];

			for (let i = 0; i < this.tagAll.length; i++) {
				this.tags.push(this.tagAll[i]);
			}
		},

		// 头像上传前校验，宽高不相等时，无法阻止上传
		beforeAvatarUpload(file) {
		  const allowedTypes = ['jpg', 'png', 'jpeg'];
		  const fileSuffix = file.name.split('.').pop().toLowerCase();
      const isValidType = allowedTypes.includes(fileSuffix);
      const isLt2M = file.size / 1024 / 1024 <= 2;

      this.uploadLoading = Loading.service({
		    fullscreen: true,
	      customClass: 'text-loading',
	      text: 'Uploading...'
		  });

			if (!isValidType) {
      	this.uploadLoading.close();
      	this.$message({
		      message: `Invalid file format. Only ${allowedTypes.join('、')} formats are supported.`,
		      type: 'error',
		      showClose: true,
		      customClass: 'custom-message',
		    });
      	return false;
      }

      if (!isLt2M) {
      	this.uploadLoading.close();
      	this.$message({
		      message: `The file size must not exceed 2MB.`,
		      type: 'error',
		      showClose: true,
		      customClass: 'custom-message',
		    });
      	return false;
      }

      return true;
		},

		// 头像上传成功
		handleAvatarSuccess(res, file, fileList) {
			this.uploadLoading.close();

			// 上传失败
			if (res.statusCode !== 200) {
				this.$message({
		      message: res.message,
		      type: 'error',
		      showClose: true,
		      customClass: 'custom-message',
		    });
				return;
			}

			const { width, height, url, size } = res.data;

			if (width !== height) {
				this.$message({
		      message: `The image must have equal width and height. Current dimensions: ${width}x${height}`,
		      type: 'error',
		      showClose: true,
		      customClass: 'custom-message',
		    });
				return;
			}

			this.formData.avatar_url = url;
			this.formData.avatar_width = width;
			this.formData.avatar_height = height;
			this.formData.avatar_size = size;
			this.$refs.createRoleForm.validateField('avatar_url');
		},

		// 头像上传失败
		handleAvatarError() {
			this.formData.avatar = {
				width: 0,
				height: 0,
				size: 0,
				url: ''
			};
			this.$message({
	      message: `Upload file fail.`,
	      type: 'error',
	      showClose: true,
	      customClass: 'custom-message',
	    });
			this.uploadLoading.close();
		},

		// 画廊上传
		beforeGalleriesUpload(file) {
			const allowedTypes = ['jpg', 'png', 'jpeg'];
      const fileSuffix = file.name.split('.').pop().toLowerCase();
      const isValidType = allowedTypes.includes(fileSuffix);
      const isLt2M = file.size / 1024 / 1024 <= 2;

      this.uploadLoading = Loading.service({
		    fullscreen: true,
	      customClass: 'text-loading',
	      text: 'Uploading...'
		  });

      if (!isValidType) {
      	this.uploadLoading.close();
      	this.$message({
		      message: `Invalid file format. Only ${allowedTypes.join('、')} formats are supported.`,
		      type: 'error',
		      showClose: true,
		      customClass: 'custom-message',
		    });
      	return false;
      }

      if (!isLt2M) {
      	this.uploadLoading.close();
      	this.$message({
		      message: `The file size must not exceed 2MB.`,
		      type: 'error',
		      showClose: true,
		      customClass: 'custom-message',
		    });
      	return false;
      }

      return true;
		},

		handleGalleriesSuccess(res, file, fileList) {
			this.uploadLoading.close();

			// 上传失败
			if (res.statusCode !== 200) {
				this.$message({
		      message: res.message,
		      type: 'error',
		      showClose: true,
		      customClass: 'custom-message',
		    });
				return;
			}

			const { width, height, url, preview_url, size } = res.data;

			if (width >= height) {
				this.$message({
		      message: `Image width should be less than the height. Current dimensions: ${width}x${height}`,
		      type: 'error',
		      showClose: true,
		      customClass: 'custom-message',
		    });
				return;
			}

			this.formData.galleries.push({
				uid: `${file.uid}`,
				url,
				preview_url,
				width,
				height,
				size
			});
			this.$refs.createRoleForm.validateField('galleries');
		},

		handleGalleriesError(err, file, fileList) {
			this.uploadLoading.close();
			this.$message({
	      message: `Upload file fail.`,
	      type: 'error',
	      showClose: true,
	      customClass: 'custom-message',
	    });
		},

		handleGalleriesChange(file, fileList) {
			this.$refs.createRoleForm.validateField('galleries');
		},

		delGallery(uid) {
			for (let i = 0; i < this.formData.galleries.length; i++) {
				if (this.formData.galleries[i].uid === uid) {
					this.formData.galleries.splice(i, 1);
					i--;
				}
			}

			this.$refs.createRoleForm.validateField('galleries');
		},

		addFileToGalleries(file) {
			for (let i = 0; i < this.formData.galleries.length; i++) {
				if (this.formData.galleries[i].uid === file.uid) {
					return;
				}
			}

			this.formData.galleries.push(file);
		},

		removeFromGalleries(file) {
			for (let i = 0; i < this.formData.galleries.length; i++) {
				if (this.formData.galleries[i].uid === file.uid) {
					this.formData.galleries.splice(i, 1);
					i--;
				}
			}
		},

		// 手动设置字段错误
    setFieldError(fieldName, errorMessage) {
      if (!errorMessage) return;
      
      // 找到对应的表单项
      const field = this.$refs.createRoleForm.fields.find(
        f => f.prop === fieldName
      );
      
      if (field) {
        // 清除原有校验状态
        field.clearValidate();
        
        // 设置错误状态
        field.validateState = 'error';
        field.validateMessage = errorMessage;
        
        // 更新视图
        this.$forceUpdate();
      }
    },

    // 清空指定表单项的错误
    clearTargetFieldError(fieldName) {
    	// 找到对应的表单项
      const field = this.$refs.createRoleForm.fields.find(
        f => f.prop === fieldName
      );
      
      if (field) {
        // 清除原有校验状态
        field.clearValidate();
        
        // 更新视图
        this.$forceUpdate();
      }
    },

    validateForm() {
    	return new Promise((resolve) => {
    		this.$refs.createRoleForm.validate((valid) => {
    			resolve(valid);
		    });
    	});
    },

    handleSubmitTips(button) {
    	if (button.id === 'ok') {
    		this.$router.replace({
		    	path: '/user_center',
		    	query: {
	          invite_code: this.inviteCode,
	        }
		    });
    	}
    },

    async submit() {
    	const formStatus = await this.validateForm();

    	if (!formStatus) {
    		this.$message({
		      message: `Please check your form inputs.`,
		      type: 'error',
		      showClose: true,
		      customClass: 'custom-message',
		    });
		    return;
    	}

    	this.submitLoading = Loading.service({
		    fullscreen: true,
	      customClass: 'text-loading',
	      text: 'Submitting...'
		  });

		  const { edit, character_id } = this.$route.query;

    	try {
    		const res = await createRole({
    			...this.formData,
    			token: this.userToken,
    			is_edit: Boolean(edit),
    			character_id
    		});

    		this.submitLoading.close();

    		if (res.statusCode === 200) {
    			// this.$message({
			    //   message: 'Submitted successfully; please wait for administrator review.',
			    //   type: 'success',
			    //   showClose: true,
			    //   customClass: 'custom-message',
			    // });
			    
			    this.$refs.submitTips.show();
			    return;
    		}

    		this.$message({
		      message: `Create character fail.`,
		      type: 'error',
		      showClose: true,
		      customClass: 'custom-message',
		    });
    	}
    	catch(err) {
    		this.$message({
		      message: `Create character fail.`,
		      type: 'error',
		      showClose: true,
		      customClass: 'custom-message',
		    });
    	}

    	this.submitLoading.close();
    }
	},

	activated() {
		report({
      event_type: 'view',
      event_value: 'create_character'
    });
    this.init();
	},

	components: {
		Navigation,
		OpenApp,
		Button,
		Dialog
	}
};
